// lib/sentry-base.ts
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from "@/lib/constants";
import type { Integration } from "@sentry/core";
import Logger from '@/lib/logger';

const logger = new Logger('pbs:sentry:base:config');
logger.info("Initializing Sentry base config ...");

export interface SentryConfig {
  dsn: string;
  environment: string;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  debug: boolean;
  integrations: Integration[];
}

export const defaultInit: Omit<SentryConfig, 'environment'> = {
  dsn: SENTRY_DSN,
  sampleRate: 0.0,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.0,
  debug: false,
  integrations: [],
};

export function createSentryConfig(overrides: Partial<SentryConfig> = {}): SentryConfig {
  const config: SentryConfig = {
    ...defaultInit,
    environment: "development",
    ...overrides
  };

  switch (SENTRY_ENVIRONMENT) {
    case "edcar":
      logger.debug("Overriding Sentry base config with edcar settings...");
      return {
        ...config,
        environment: "edcar",
        sampleRate: 0.5,
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1.0,
      };
    case "staging":
      logger.debug("Overriding Sentry base config with staging settings...");
      return {
        ...config,
        environment: "staging",
        // TODO: Add staging-specific config
      };
    case "production":
      logger.debug("Overriding Sentry base config with production settings...");
      return {
        ...config,
        environment: "production",
        sampleRate: 0.25,
        tracesSampleRate: 1,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 1.0,
      };
    default:
      logger.debug("Accepting Sentry default base config without modification...");
      return {
        ...config,
        environment: "development",
      };
  }
}